import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { PayerSubmissionReturn } from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canSubmitPayerFile } from './constants/UserTypes';
import { getBankErrorParts } from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import PayerFileDialog from './PayerFileDialog';

interface HoaPayerFileListProps {
  hoaId: string;
}

interface AlertState {
  title: string;
  message: string;
}

// Status → Catalyst badge color. `processing` is the normal resting state for
// an accepted upload — the bank validates the records separately — so it is
// styled as neutral/pending, never as an error. Only `failed` is bad.
const statusColor = (status: string): 'lime' | 'red' | 'amber' | 'zinc' => {
  switch (status) {
    case 'complete':
      return 'lime';
    case 'failed':
      return 'red';
    case 'processing':
      return 'amber';
    default:
      return 'zinc'; // building / unknown
  }
};

const statusLabel = (status: string): string =>
  status.charAt(0).toUpperCase() + status.slice(1);

// Submissions are timestamped rather than dated — a retry after a rejected
// file lands the same day, so the shared date-only formatter is not enough.
const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

const HoaPayerFileList: React.FC<HoaPayerFileListProps> = ({ hoaId }) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canSubmit = useMemo(() => canSubmitPayerFile(userType), [userType]);

  const [submissions, setSubmissions] = useState<PayerSubmissionReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isPayerOpen, setIsPayerOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setAlert(null);
      const resp = await bankService.listPayerSubmissions({ hoaId });
      setSubmissions(resp.data.submissions);
    } catch (err) {
      console.error('Error fetching payer file submissions:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        title: "Couldn't load payer files",
        message: body || 'Please try again.',
      });
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Download the bytes exactly as they were sent (a stored snapshot, not a
  // fresh preview — a preview re-assembles from current data and can drift).
  // The generated method types the binary body as `void`, so the response is
  // taken as a blob, mirroring the statement-PDF download.
  const downloadFile = async (s: PayerSubmissionReturn) => {
    try {
      setDownloadingId(s.id);
      setAlert(null);
      const { data } = await bankService.getPayerSubmissionFile(
        { submissionId: s.id },
        { responseType: 'blob' as const }
      );
      const blob = data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = s.file_name || `payer-file-${s.id}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading payer file:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        title: "Couldn't download the file",
        message: body || 'Please try again.',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!canSubmit) return null;

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 mb-4">
        <div>
          <Heading level={4}>Payer Files</Heading>
          <Text className="mt-1 text-sm">
            The lockbox roster sent to the bank for this HOA — one record per
            home. “Processing” means the bank accepted the file and is
            validating the records.
          </Text>
        </div>
        {canSubmit && (
          <Button color="sky" onClick={() => setIsPayerOpen(true)}>
            <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
            Generate &amp; Submit Payer File
          </Button>
        )}
      </div>

      {alert && (
        <ShowAlert
          alertType="error"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={8000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : submissions.length === 0 ? (
            <Text>
              No payer files sent yet.
              {canSubmit
                ? ' Use “Generate & Submit Payer File” to build one and send it to the bank.'
                : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Submitted
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          File
                        </TableHeader>
                        <TableHeader className="text-base font-semibold text-right">
                          Records
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Reference
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          <span className="sr-only">Download</span>
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {submissions.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            {formatDateTime(s.submitted_at || s.date_created)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {s.file_name}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {s.record_count}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {s.reference_id}
                          </TableCell>
                          <TableCell>
                            <Badge color={statusColor(s.status)}>
                              {statusLabel(s.status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              plain
                              onClick={() => downloadFile(s)}
                              disabled={downloadingId === s.id}
                            >
                              {downloadingId === s.id
                                ? 'Downloading…'
                                : 'Download'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <PayerFileDialog
        open={isPayerOpen}
        onClose={() => setIsPayerOpen(false)}
        hoaId={hoaId}
        onCompleted={fetchSubmissions}
      />
    </>
  );
};

export default HoaPayerFileList;
